import type { Resolver } from '@Core/lib/tasks/Resolve';

import { VariableType } from '@/types/enums';

export type VariablePickerContext = Record<string, unknown>;

export type LogicalOperator = 'and' | 'or' | 'nor';

export type FilterGroup = {
  operator: LogicalOperator;
  conditions: FilterNode[];
  when?: boolean | Resolver | null;
};

export type FilterCondition = {
  field: unknown;
  operator: string;
  value: unknown;
  when?: boolean | Resolver | null;
};

export type FilterNode = FilterGroup | FilterCondition;

export type FilterOperatorData = {
  label: string;
  value: string;
  value_shape: Diagonal.Core.Enums.FilterValueShape;
};

export type SchemaField = {
  key: string;
  label: string | null;
  type: VariableType;
  settings?: Record<string, unknown> | null;
};

export type FlattenedField = {
  path: string;
  label: string;
  type: VariableType;
  schema: SchemaField;
  resource?: string | null;
  /** When built from workflow variables, resolver used as the left operand for guard filter checks. */
  variableResolver?: Record<string, unknown> | null;
};

export type NotOperatorValue = {
  operator: string;
  value: unknown;
};

export function isFilterGroup(node: FilterNode): node is FilterGroup {
  return 'conditions' in node && Array.isArray((node as FilterGroup).conditions);
}

export function isFilterCondition(node: FilterNode): node is FilterCondition {
  return 'field' in node && !('conditions' in node);
}

export function isNotOperatorValue(value: unknown): value is NotOperatorValue {
  return typeof value === 'object' && value !== null && 'operator' in value;
}

/** @see StoredFilter — deliberately opaque, not {@see FilterNode}. */
type OpaqueFilterNode = Record<string, never>;

/**
 * A filter as it is persisted on a setting. Editors write a {@see FilterGroup},
 * but `CompileWorkflowCode` writes the flat condition list code mode authors, so
 * anything reading a stored filter has to accept both — mirroring the backend's
 * `FilterGroupData::fromArray()`.
 *
 * The nodes stay opaque: these values live in Inertia forms, whose `FormDataType`
 * constraint cannot express `FilterNode`'s recursion. Read one through
 * {@see toFilterGroup} and write one through {@see toStoredFilter} rather than
 * reaching into it.
 */
export type StoredFilter =
  | { operator: string; conditions: OpaqueFilterNode[] }
  | OpaqueFilterNode[]
  | null;

/**
 * Normalize a persisted filter into a group, or null when it holds no
 * conditions. A bare list is an implicit `and`, as the backend treats it.
 */
export function toFilterGroup(value: StoredFilter | undefined): FilterGroup | null {
  if (!value) {
    return null;
  }

  if (Array.isArray(value)) {
    return value.length === 0
      ? null
      : { operator: 'and', conditions: value as unknown as FilterNode[] };
  }

  const group = value as unknown as FilterGroup;

  return isFilterGroup(group) ? group : null;
}

/**
 * Store a group back on a setting, or null when it holds no conditions. The cast
 * is the same `FormDataType` limitation {@see StoredFilter} documents, kept here
 * so callers never write one themselves.
 */
export function toStoredFilter(group: FilterGroup | null): StoredFilter {
  return group && group.conditions.length > 0 ? (group as unknown as StoredFilter) : null;
}

export function createEmptyGroup(operator: LogicalOperator = 'and'): FilterGroup {
  return { operator, conditions: [] };
}

export function createEmptyCondition(): FilterCondition {
  return { field: '', operator: '=', value: '' };
}

export function toHeadline(str: string): string {
  return str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
}

export function flattenSchemaFields(
  schema: SchemaField[],
  prefix: string = '',
  labelSegments: string[] = [],
  includeRelations: boolean = true,
  resource: string | null = null
): FlattenedField[] {
  const fields: FlattenedField[] = [];

  for (const field of schema) {
    if (field.type === VariableType.Relation) {
      if (!includeRelations) {
        continue;
      }

      const path = prefix ? `${prefix}.${field.key}` : field.key;
      const segments = [...labelSegments, toHeadline(field.label || field.key)];
      const relationResourceId =
        typeof field.settings?.resource_id === 'string' ? field.settings.resource_id : null;
      const nestedSchema = field.settings?.schema;

      if (Array.isArray(nestedSchema)) {
        fields.push(
          ...flattenSchemaFields(
            nestedSchema as SchemaField[],
            path,
            segments,
            includeRelations,
            relationResourceId
          )
        );
        continue;
      }

      if (
        nestedSchema &&
        typeof nestedSchema === 'object' &&
        (nestedSchema as { type?: VariableType }).type === VariableType.List
      ) {
        const listItemSchema = (nestedSchema as { settings?: Record<string, unknown> }).settings
          ?.schema;
        const recordSchema = (listItemSchema as { settings?: Record<string, unknown> } | undefined)
          ?.settings?.schema;

        if (Array.isArray(recordSchema)) {
          fields.push(
            ...flattenSchemaFields(
              recordSchema as SchemaField[],
              path,
              segments,
              includeRelations,
              relationResourceId
            )
          );
        }
      }

      continue;
    }

    if (field.settings?.virtual === true) {
      continue;
    }

    const path = prefix ? `${prefix}.${field.key}` : field.key;
    const segments = [...labelSegments, toHeadline(field.label || field.key)];
    const label = segments.join(' > ');

    fields.push({
      path,
      label,
      type: field.type,
      schema: field,
      resource: field.key === 'id' ? resource : null
    });

    let nestedSchema = field.settings?.schema;

    // A list's nested schema is a single item definition wrapping the real
    // field list (see ListVariable), so unwrap one level.
    if (
      field.type === VariableType.List &&
      nestedSchema &&
      typeof nestedSchema === 'object' &&
      !Array.isArray(nestedSchema)
    ) {
      nestedSchema = (nestedSchema as { settings?: Record<string, unknown> }).settings?.schema;
    }

    if (Array.isArray(nestedSchema)) {
      fields.push(
        ...flattenSchemaFields(nestedSchema as SchemaField[], path, segments, includeRelations)
      );
    }
  }

  return fields;
}

export function countConditions(node: FilterGroup): number {
  return node.conditions.reduce(
    (count, child) => count + (isFilterGroup(child) ? countConditions(child) : 1),
    0
  );
}

/**
 * Initializes an editable filter draft from a persisted value. Valid
 * `FilterGroup` inputs are cloned with `structuredClone` so edits do not mutate
 * the source. Empty groups are seeded with one blank condition so the dialog
 * opens with a row the user can edit immediately.
 */
export function createEditableFilterDraft(
  value: unknown,
  operator: LogicalOperator = 'and'
): FilterGroup {
  const parsed =
    value && typeof value === 'object' && isFilterGroup(value as FilterGroup)
      ? structuredClone(value as FilterGroup)
      : createEmptyGroup(operator);

  if (parsed.conditions.length === 0) {
    return { ...parsed, conditions: [createEmptyCondition()] };
  }

  return parsed;
}

/**
 * Apply-boundary sanitizer used when committing an editable draft back to the
 * persisted value. Drops conditions whose `field` is empty, recursively removes
 * empty groups, and returns `null` if no valid conditions remain.
 */
export function sanitizeApplyableFilterGroup(group: FilterGroup | null): FilterGroup | null {
  return sanitizeFilterGroupWithOptions(group);
}

/**
 * Recursively strips incomplete conditions (empty field) and empty groups,
 * returning null if the top-level group has no valid conditions remaining.
 */
export function sanitizeFilterGroup(group: FilterGroup | null): FilterGroup | null {
  return sanitizeApplyableFilterGroup(group);
}

export function sanitizeFilterGroupForVariables(group: FilterGroup | null): FilterGroup | null {
  return sanitizeFilterGroupWithOptions(group, { allowObjectFields: true });
}

function sanitizeFilterGroupWithOptions(
  group: FilterGroup | null,
  options: { allowObjectFields?: boolean } = {}
): FilterGroup | null {
  if (!group) {
    return null;
  }

  const sanitized: FilterNode[] = [];

  for (const node of group.conditions) {
    if (isFilterGroup(node)) {
      const child = sanitizeFilterGroupWithOptions(node, options);

      if (child) {
        sanitized.push(child);
      }
    } else if (
      isFilterCondition(node) &&
      ((typeof node.field === 'string' && node.field.trim() !== '') ||
        (options.allowObjectFields === true &&
          typeof node.field === 'object' &&
          node.field !== null))
    ) {
      sanitized.push(node);
    }
  }

  if (sanitized.length === 0) {
    return null;
  }

  return { ...group, conditions: sanitized };
}
